import { useEffect, useMemo, useRef, useState, type FormEvent, type KeyboardEvent, type PointerEvent as ReactPointerEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { Link, useLocation } from 'react-router-dom';
import {
  assistantApi,
  type AssistantAdvisory, type AssistantAudioReply, type AssistantConversation, type AssistantMessage,
  type AssistantConfiguration, type AssistantContentType, type AssistantSchedule, type AssistantTrace,
} from '../../shared/api/assistant';
import { ApiRequestError } from '../../shared/api/http';
import { Icon, type IconName } from '../../shared/components/Icon';
import { getActiveLocale, type AppLocale } from '../../shared/i18n/i18n';
import { speechLocaleTag } from '../../shared/i18n/localeTags';
import { calendarLocaleTag } from '../../shared/lib/calendar';
import { formatRelativeTime } from '../../shared/lib/formatters';
import { normalizeSearchText } from '../../shared/lib/search';
import { AssistantResourcePanel } from './AssistantResourcePanel';

interface AssistantPanelProps {
  open: boolean;
  onClose: () => void;
  /** Docked, the assistant is a column of the shell rather than a sheet laid over it. */
  docked?: boolean;
  width?: number;
  onResize?: (width: number) => void;
  /*
   * A shell that has its own way into the assistant — the modern desktop's mark, which can be
   * spoken to without this panel ever being opened — owns the conversation instead, and passes
   * it in. Left undefined, the panel keeps its own, which is what the classic shell does.
   */
  conversationId?: string | null;
  onConversationChange?: (id: string | null) => void;
  /** Reported so a shell can show that the assistant is working while the panel is shut. */
  onBusyChange?: (busy: boolean) => void;
  /*
   * Set when the shell around the panel carries the microphone and the speaker itself. The
   * panel then stops offering its own, because two recorders on one machine is not twice the
   * feature: the shell's is listening continuously, and it would hear the reply this one
   * played and answer it — an assistant talking to itself for as long as anybody let it.
   */
  voiceHeldByShell?: boolean;
}

/** How far one arrow key moves the sash, for a hand that is not on the pointer. */
const resizeStep = 24;

type AssistantTab = 'chat' | 'advisories' | 'history' | 'schedules' | 'memory' | 'knowledge' | 'resources';

/**
 * Two minutes is as long as a voice note is allowed to run. It is long enough to describe a transaction
 * out loud and short enough that a forgotten recorder cannot fill a request with an hour of room noise.
 * The recorder stops itself at the limit rather than refusing the upload afterwards, so nobody loses a
 * message they have already spoken.
 */
const MAXIMUM_RECORDING_MILLISECONDS = 120_000;

interface RecorderSession {
  instance: MediaRecorder; chunks: Blob[]; stream: MediaStream; discarded: boolean;
}

interface SpeechResultEvent { results: { readonly [index: number]: { readonly [index: number]: { transcript: string } } } }
interface SpeechRecognitionLike {
  lang: string; continuous: boolean; interimResults: boolean;
  onresult: ((event: SpeechResultEvent) => void) | null;
  onend: (() => void) | null; onerror: (() => void) | null;
  start: () => void; stop: () => void;
}
type SpeechRecognitionConstructor = new () => SpeechRecognitionLike;

function formatDuration(milliseconds: number): string {
  const total = Math.max(0, Math.round(milliseconds / 1_000));
  return `${Math.floor(total / 60)}:${(total % 60).toString().padStart(2, '0')}`;
}

function formatClock(value: string, locale: AppLocale): string {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '';
  return new Intl.DateTimeFormat(calendarLocaleTag(locale), { hour: '2-digit', minute: '2-digit' }).format(parsed);
}

function decodeAudioReply(reply: AssistantAudioReply): Blob {
  const binary = globalThis.atob(reply.content);
  const buffer = new ArrayBuffer(binary.length);
  const bytes = new Uint8Array(buffer);
  for (let index = 0; index < binary.length; index += 1) bytes[index] = binary.charCodeAt(index);
  return new Blob([buffer], { type: reply.contentType || 'audio/mpeg' });
}

function canConfirmMessage(
  configuration: AssistantConfiguration | undefined,
  contentType: AssistantContentType,
): boolean {
  if (!configuration) return false;
  switch (contentType) {
    case 'TransactionProposal': return configuration.canConfirmTransactions;
    case 'ReceiptProposal': return configuration.canConfirmReceipts;
    case 'InvoiceProposal': return configuration.canConfirmInvoices;
    case 'SupplierPaymentProposal': return configuration.canConfirmSupplierPayments;
    case 'BankTransferProposal': return configuration.canConfirmBankTransfers;
    case 'CashTransactionProposal': return configuration.canConfirmCashTransactions;
    case 'StatementImportProposal': return configuration.canConfirmStatementImports;
    case 'PersonProposal': return configuration.canConfirmPersons;
    default: return false;
  }
}

export function AssistantPanel({
  open, onClose, docked = false, width = 0, onResize,
  conversationId, onConversationChange, onBusyChange, voiceHeldByShell = false,
}: AssistantPanelProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const locale = getActiveLocale();
  const location = useLocation();
  const experienceCode = useMemo(() => {
    const value = new URLSearchParams(location.search).get('experience')?.trim();
    return value || undefined;
  }, [location.search]);
  const [tab, setTab] = useState<AssistantTab>('chat');
  const [selectedTraceId, setSelectedTraceId] = useState<string | null>(null);
  const [ownConversationId, setOwnConversationId] = useState<string | null>(null);
  /*
   * Controlled when the shell passes a conversation, uncontrolled otherwise. The two are kept
   * behind one name and one setter so nothing below this line has to know which it is.
   */
  const controlled = conversationId !== undefined;
  const activeId = controlled ? conversationId : ownConversationId;
  const setActiveId = (id: string | null) => {
    if (controlled) onConversationChange?.(id);
    else setOwnConversationId(id);
  };
  const [draft, setDraft] = useState('');
  const [pendingMessage, setPendingMessage] = useState<string | null>(null);
  const [streamedContent, setStreamedContent] = useState('');
  const [sendError, setSendError] = useState('');
  const [recording, setRecording] = useState(false);
  const [recordedMilliseconds, setRecordedMilliseconds] = useState(0);
  const [transcribing, setTranscribing] = useState(false);
  const [audioReply, setAudioReply] = useState(false);
  const [voiceError, setVoiceError] = useState('');
  const [decidedProposalIds, setDecidedProposalIds] = useState<ReadonlySet<string>>(() => new Set());
  const messagesEnd = useRef<HTMLDivElement>(null);
  const composer = useRef<HTMLTextAreaElement>(null);
  const panel = useRef<HTMLElement>(null);
  const recorder = useRef<RecorderSession | null>(null);
  const recognizer = useRef<SpeechRecognitionLike | null>(null);
  const recordingClock = useRef<number | null>(null);

  const configuration = useQuery({
    queryKey: ['assistant', 'configuration'], queryFn: assistantApi.configuration, enabled: open, retry: false,
  });
  const conversations = useQuery({
    queryKey: ['assistant', 'conversations'], queryFn: assistantApi.conversations, enabled: open, retry: false,
  });
  const messages = useQuery({
    queryKey: ['assistant', 'messages', activeId],
    queryFn: () => assistantApi.messages(activeId ?? ''), enabled: open && activeId !== null,
  });
  // Traces need `assistant.trace.read`, so asking for them without it bought a guaranteed 403 on every
  // conversation switch. Proposal state still reads correctly from the message itself without them.
  const traces = useQuery({
    queryKey: ['assistant', 'traces', activeId], queryFn: () => assistantApi.traces(activeId ?? undefined),
    enabled: open && activeId !== null && configuration.data?.canInspectEvidence === true, retry: false,
  });
  const schedules = useQuery({
    queryKey: ['assistant', 'schedules'], queryFn: assistantApi.schedules,
    enabled: open && tab === 'schedules' && configuration.data?.canManageSchedules === true, retry: false,
  });
  const selectedTrace = useQuery({
    queryKey: ['assistant', 'trace', selectedTraceId], queryFn: () => assistantApi.trace(selectedTraceId ?? ''),
    enabled: open && selectedTraceId !== null && configuration.data?.canInspectEvidence === true, retry: false,
  });
  const usage = useQuery({
    queryKey: ['assistant', 'usage'], queryFn: assistantApi.usage,
    enabled: open && configuration.data?.canInspectEvidence === true, retry: false,
  });

  useEffect(() => {
    if (activeId === null && conversations.data?.[0]) setActiveId(conversations.data[0].id);
  }, [activeId, conversations.data]);
  useEffect(() => { setDecidedProposalIds(new Set()); }, [activeId]);
  useEffect(() => { messagesEnd.current?.scrollIntoView({ behavior: 'smooth' }); },
    [messages.data, pendingMessage, streamedContent]);
  useEffect(() => { if (open && tab === 'chat') composer.current?.focus(); }, [open, tab]);
  useEffect(() => {
    if (!open) {
      setRecording(false);
      setTranscribing(false);
    }
    return () => {
      if (recordingClock.current !== null) window.clearInterval(recordingClock.current);
      recordingClock.current = null;
      const active = recorder.current;
      if (active === null) return;
      active.discarded = true;
      active.instance.ondataavailable = null;
      active.instance.onstop = null;
      active.instance.onerror = null;
      if (active.instance.state !== 'inactive') active.instance.stop();
      active.stream.getTracks().forEach((track) => track.stop());
      recorder.current = null;
    };
  }, [open]);

  // Escape unwinds one layer at a time: the evidence drawer, then a recording in progress, then the panel.
  // Closing the whole assistant while somebody is mid-sentence would throw away what they had said.
  //
  // Docked, the assistant no longer owns the screen, so Escape belongs to whatever has focus. It only
  // unwinds the assistant when the assistant is the thing being worked in.
  useEffect(() => {
    if (!open) return undefined;
    const dismiss = (event: globalThis.KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      if (docked && !panel.current?.contains(document.activeElement)) return;
      if (selectedTraceId !== null) { setSelectedTraceId(null); return; }
      if (recorder.current !== null) { recorder.current.discarded = true; recorder.current.instance.stop(); return; }
      onClose();
    };
    window.addEventListener('keydown', dismiss);
    return () => window.removeEventListener('keydown', dismiss);
  }, [open, selectedTraceId, onClose, docked]);

  const playAudio = async (blob: Blob) => {
    const url = URL.createObjectURL(blob);
    const audio = new Audio(url);
    audio.onended = () => URL.revokeObjectURL(url);
    audio.onerror = () => URL.revokeObjectURL(url);
    try {
      await audio.play();
    } catch (error) {
      URL.revokeObjectURL(url);
      throw error;
    }
  };

  const speak = async (message: AssistantMessage) => {
    const text = message.contentType === 'HtmlReport'
      ? new DOMParser().parseFromString(message.content, 'text/html').body.textContent ?? ''
      : message.content;
    if (configuration.data?.textToSpeechEnabled) {
      await playAudio(await assistantApi.speech(text, locale));
      return;
    }
    if ('speechSynthesis' in window) {
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.lang = speechLocaleTag(locale);
      window.speechSynthesis.speak(utterance);
    }
  };

  const createConversation = useMutation({
    mutationFn: () => assistantApi.createConversation(),
    onSuccess: (item) => {
      setActiveId(item.id);
      void queryClient.invalidateQueries({ queryKey: ['assistant', 'conversations'] });
    },
  });
  const sendMessage = useMutation({
    mutationFn: async (content: string) => {
      const id = activeId ?? (await assistantApi.createConversation()).id;
      if (activeId === null) setActiveId(id);
      return assistantApi.sendStream(id, content, locale, (event) => {
        if (event.event !== 'content') return;
        const delta = (event.data as { delta?: unknown }).delta;
        if (typeof delta === 'string') setStreamedContent((current) => current + delta);
      }, experienceCode);
    },
    onSuccess: async (item) => {
      await queryClient.invalidateQueries({ queryKey: ['assistant', 'messages', item.conversationId] });
      setPendingMessage(null);
      setStreamedContent('');
      setSendError('');
      void queryClient.invalidateQueries({ queryKey: ['assistant', 'conversations'] });
      void queryClient.invalidateQueries({ queryKey: ['assistant', 'traces', item.conversationId] });
      if (audioReply && configuration.data?.textToSpeechEnabled) {
        setVoiceError('');
        void speak(item).catch(() => setVoiceError(t('assistant.speechFailed')));
      }
    },
    onError: (_error: unknown, content) => {
      setPendingMessage(null);
      setStreamedContent('');
      setDraft((current) => current.trim() ? current : content);
      setSendError(t('assistant.sendFailed'));
      window.requestAnimationFrame(() => composer.current?.focus());
    },
  });
  const sendVoice = useMutation({
    mutationFn: async (request: { audio: Blob; includeAudioReply: boolean }) => {
      const id = activeId ?? (await assistantApi.createConversation()).id;
      if (activeId === null) setActiveId(id);
      return assistantApi.sendVoice(id, request.audio, locale, request.includeAudioReply, experienceCode);
    },
    onSuccess: (turn, request) => {
      queryClient.setQueryData<readonly AssistantMessage[]>(
        ['assistant', 'messages', turn.message.conversationId],
        (current) => {
          const messagesById = new Map((current ?? []).map((item) => [item.id, item]));
          messagesById.set(turn.transcriptMessage.id, turn.transcriptMessage);
          messagesById.set(turn.message.id, turn.message);
          return [...messagesById.values()].sort((left, right) =>
            left.createdAt.localeCompare(right.createdAt) || left.id.localeCompare(right.id));
        },
      );
      void queryClient.invalidateQueries({ queryKey: ['assistant', 'messages', turn.message.conversationId] });
      void queryClient.invalidateQueries({ queryKey: ['assistant', 'conversations'] });
      void queryClient.invalidateQueries({ queryKey: ['assistant', 'traces', turn.message.conversationId] });
      setVoiceError('');
      if (request.includeAudioReply) {
        if (turn.audio) {
          void playAudio(decodeAudioReply(turn.audio)).catch(() => setVoiceError(t('assistant.speechFailed')));
        } else {
          setVoiceError(t('assistant.speechFailed'));
        }
      }
    },
    // The server distinguishes "we heard nothing" from "the recording could not be processed"; a speaker
    // who was simply too far from the microphone should be told to try again, not shown a failure.
    onError: (error: unknown) => setVoiceError(error instanceof ApiRequestError && error.code === 'assistant.audio_empty'
      ? t('assistant.voiceEmpty')
      : t('assistant.voiceFailed')),
    onSettled: () => setTranscribing(false),
  });
  const confirmProposal = useMutation({
    mutationFn: assistantApi.approveAction,
    onSuccess: (action) => {
      setDecidedProposalIds((current) => new Set(current).add(action.id));
      void queryClient.invalidateQueries({ queryKey: ['assistant', 'messages', activeId] });
      void queryClient.invalidateQueries({ queryKey: ['assistant', 'traces', activeId] });
      void queryClient.invalidateQueries({ queryKey: ['persons'] });
      if (selectedTraceId && configuration.data?.canInspectEvidence) void selectedTrace.refetch();
    },
  });
  const rejectProposal = useMutation({
    mutationFn: (proposalId: string) => assistantApi.rejectAction(proposalId, t('assistant.reviewerRejected')),
    onSuccess: (_result, proposalId) => {
      setDecidedProposalIds((current) => new Set(current).add(proposalId));
      if (selectedTraceId && configuration.data?.canInspectEvidence) void selectedTrace.refetch();
      void queryClient.invalidateQueries({ queryKey: ['assistant', 'messages', activeId] });
    },
  });

  const busy = sendMessage.isPending || sendVoice.isPending || transcribing;

  /*
   * Reported outward so a shell that draws its own light for the assistant — the modern
   * desktop's mark does — is lit by a question typed here exactly as it is by a spoken one.
   */
  useEffect(() => {
    onBusyChange?.(busy);
    // Closed mid-request, the panel takes its light with it rather than leaving one lit in a
    // shell that has no way of knowing the request it belonged to ever finished.
    return () => onBusyChange?.(false);
  }, [busy, onBusyChange]);
  const disabled = configuration.data?.isEnabled === false;
  // Until the configuration arrives there is no way to know whether the server can transcribe, and guessing
  // wrong drops the speaker into the browser's own recognizer and an error they did not earn.
  const voiceReady = configuration.data !== undefined;

  const send = (content: string) => {
    const spoken = content.trim();
    if (!spoken || busy) return;
    setSendError('');
    setPendingMessage(spoken);
    setStreamedContent('');
    setDraft('');
    sendMessage.mutate(spoken);
  };
  const submit = (event: FormEvent) => {
    event.preventDefault();
    send(draft);
  };
  /** Enter sends, the way every other chat does; Shift+Enter is how a longer description gets its lines. */
  const composerKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key !== 'Enter' || event.shiftKey || event.nativeEvent.isComposing) return;
    event.preventDefault();
    send(draft);
  };

  const stopClock = () => {
    if (recordingClock.current !== null) window.clearInterval(recordingClock.current);
    recordingClock.current = null;
  };

  /** Ends the take. Discarded recordings are dropped where they are; the rest go straight to Varza. */
  const finishRecording = (discard: boolean) => {
    const session = recorder.current;
    if (session === null) {
      recognizer.current?.stop();
      recognizer.current = null;
      setRecording(false);
      return;
    }
    session.discarded = discard;
    if (session.instance.state === 'inactive') return;
    session.instance.stop();
  };

  const startRecording = async () => {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    const chunks: Blob[] = [];
    const preferredType = typeof MediaRecorder.isTypeSupported === 'function' ? [
      'audio/webm;codecs=opus', 'audio/mp4;codecs=mp4a.40.2', 'audio/mp4', 'audio/webm',
    ].find((type) => MediaRecorder.isTypeSupported(type)) : undefined;
    const instance = preferredType === undefined
      ? new MediaRecorder(stream)
      : new MediaRecorder(stream, { mimeType: preferredType });
    const session: RecorderSession = { instance, chunks, stream, discarded: false };
    recorder.current = session;
    instance.ondataavailable = (event) => { if (event.data.size > 0) chunks.push(event.data); };
    instance.onerror = () => {
      stopClock();
      stream.getTracks().forEach((track) => track.stop());
      recorder.current = null;
      setRecording(false);
      setRecordedMilliseconds(0);
      setVoiceError(t('assistant.voiceFailed'));
    };
    instance.onstop = () => {
      stopClock();
      stream.getTracks().forEach((track) => track.stop());
      recorder.current = null;
      setRecording(false);
      setRecordedMilliseconds(0);
      if (session.discarded) return;
      const audio = new Blob(chunks, { type: instance.mimeType || 'audio/webm' });
      if (audio.size === 0) {
        setVoiceError(t('assistant.voiceEmpty'));
        return;
      }
      setTranscribing(true);
      sendVoice.mutate({
        audio,
        includeAudioReply: audioReply && configuration.data?.textToSpeechEnabled === true,
      });
    };
    const startedAt = Date.now();
    // A one-second timeslice keeps the take arriving in pieces rather than as one buffer handed over at the
    // end, which is what some browsers need before they will produce anything for a long recording at all.
    instance.start(1_000);
    setRecording(true);
    setRecordedMilliseconds(0);
    recordingClock.current = window.setInterval(() => {
      const elapsed = Date.now() - startedAt;
      setRecordedMilliseconds(Math.min(elapsed, MAXIMUM_RECORDING_MILLISECONDS));
      if (elapsed >= MAXIMUM_RECORDING_MILLISECONDS) finishRecording(false);
    }, 200);
  };

  const toggleVoice = async () => {
    setVoiceError('');
    if (recording) {
      finishRecording(false);
      return;
    }
    if (configuration.data?.speechToTextEnabled && navigator.mediaDevices
      && typeof MediaRecorder !== 'undefined') {
      try {
        await startRecording();
      } catch {
        // Refusing the microphone prompt is a choice, not a fault: say what would unblock it and stop.
        setRecording(false);
        setVoiceError(t('assistant.microphoneDenied'));
      }
      return;
    }
    // No server transcription: fall back to whatever the browser can hear, which fills the box
    // rather than sending, because on-device recognition is far less reliable.
    const speechWindow = window as Window & {
      SpeechRecognition?: SpeechRecognitionConstructor; webkitSpeechRecognition?: SpeechRecognitionConstructor;
    };
    const Constructor = speechWindow.SpeechRecognition ?? speechWindow.webkitSpeechRecognition;
    if (!Constructor) {
      setVoiceError(t('assistant.voiceFailed'));
      return;
    }
    const recognition = new Constructor();
    recognition.lang = speechLocaleTag(locale);
    recognition.continuous = false;
    recognition.interimResults = false;
    recognition.onresult = (event) => setDraft((value) => `${value} ${event.results[0]?.[0]?.transcript ?? ''}`.trim());
    recognition.onend = () => { recognizer.current = null; setRecording(false); };
    recognition.onerror = () => { recognizer.current = null; setRecording(false); };
    recognizer.current = recognition;
    recognition.start();
    setRecording(true);
  };

  if (!open) return null;
  const denied = configuration.isError || conversations.isError;
  const activeConversation = conversations.data?.find((item) => item.id === activeId);
  const tabs: readonly { key: AssistantTab; label: string; icon: IconName; shown: boolean }[] = [
    { key: 'chat', label: t('assistant.chat'), icon: 'sparkle', shown: true },
    { key: 'advisories', label: t('assistant.advisories'), icon: 'alert', shown: configuration.data?.canReadAdvisories === true },
    { key: 'history', label: t('assistant.history'), icon: 'history', shown: true },
    { key: 'schedules', label: t('assistant.schedules'), icon: 'calendar', shown: configuration.data?.canManageSchedules === true },
    { key: 'memory', label: t('assistant.memory'), icon: 'shield', shown: configuration.data?.canManageMemory === true },
    { key: 'knowledge', label: t('assistant.knowledge'), icon: 'journal', shown: configuration.data?.canManageKnowledge === true },
    { key: 'resources', label: 'Usage', icon: 'reports', shown: configuration.data?.canReadResourceUsage === true },
  ];
  return (
    <>
      {docked ? null : <button className="assistant-scrim" type="button" aria-label={t('assistant.close')} onClick={onClose} />}
      <aside
        ref={panel}
        id="varza-assistant-panel"
        className={`assistant-panel${docked ? ' assistant-panel--docked' : ''}`}
        role={docked ? undefined : 'dialog'}
        aria-modal={docked ? undefined : true}
        aria-label={t('assistant.title')}
      >
        {docked && onResize ? <AssistantSash width={width} onResize={onResize} /> : null}
        <header className="assistant-panel__header">
          <span className={`assistant-avatar${busy ? ' assistant-avatar--busy' : ''}`} aria-hidden="true">
            <span className="assistant-avatar__mark" />
          </span>
          <div><strong>{t('assistant.name')}</strong><small>{t('assistant.subtitle')}</small></div>
          <button className="icon-button" type="button" onClick={onClose} aria-label={t('assistant.close')}><Icon name="close" /></button>
        </header>
        <div className="assistant-tabs" role="tablist" aria-label={t('assistant.title')}>
          {tabs.filter((item) => item.shown).map((item) => (
            <button
              key={item.key}
              type="button"
              role="tab"
              id={`assistant-tab-${item.key}`}
              aria-selected={tab === item.key}
              aria-controls="assistant-tabpanel"
              className={tab === item.key ? 'active' : ''}
              onClick={() => setTab(item.key)}
            >
              <Icon name={item.icon} />
              <span>{item.label}</span>
            </button>
          ))}
        </div>
        {denied ? (
          <div className="assistant-empty" id="assistant-tabpanel" role="tabpanel"><Icon name="alert" /><strong>{t('assistant.noAccess')}</strong><p>{t('assistant.noAccessHint')}</p></div>
        ) : tab === 'chat' ? (
          <div className="assistant-chat" id="assistant-tabpanel" role="tabpanel" aria-labelledby="assistant-tab-chat">
            <div className="assistant-thread-tools">
              <button className="assistant-thread" type="button" onClick={() => setTab('history')} title={t('assistant.history')}>
                <Icon name="history" />
                <span>{activeConversation?.title ?? t('assistant.newConversation')}</span>
              </button>
              <button className="icon-button" type="button" onClick={() => createConversation.mutate()} aria-label={t('assistant.newConversation')}><Icon name="plus" /></button>
              <span className={`assistant-provider ${configuration.data?.isConfigured ? 'online' : ''}`}>
                {/* The chip reports readiness only: model routing is an operator concern, not a chat detail. */}
                {configuration.data?.isConfigured
                  ? `${t('assistant.connected')}${configuration.data.audioProvider === 'elevenlabs' ? ` · ${t('assistant.elevenLabsAudio')}` : ''}`
                  : t('assistant.previewMode')}
              </span>
            </div>
            <div className="assistant-messages">
              {!messages.data?.length && <AssistantWelcome onPrompt={setDraft} />}
              {messages.data?.map((message) => {
                const action = traces.data?.find((trace) => trace.id === message.traceId)?.action;
                const proposed = (action?.status ?? (message.proposalId ? 'Proposed' : '')) === 'Proposed'
                  && !decidedProposalIds.has(message.proposalId ?? '');
                const canApprove = canConfirmMessage(configuration.data, message.contentType);
                return (
                <article key={message.id} className={`assistant-message assistant-message--${message.role.toLowerCase()}`}>
                  {message.contentType === 'HtmlReport'
                    ? <div className="assistant-report" dangerouslySetInnerHTML={{ __html: message.content }} />
                    : <p>{message.content}</p>}
                  {message.journalId && <div className="assistant-artifact">
                    <Icon name="journal" /><span>{t('assistant.pendingJournal')}</span>
                    <Link to={`/console/journals/${message.journalId}`} onClick={onClose}>{t('common.view')}</Link>
                    {canApprove && proposed && message.proposalId && <button type="button" onClick={() => confirmProposal.mutate(message.proposalId ?? '')} disabled={confirmProposal.isPending}>
                      {t('assistant.confirmProposal')}
                    </button>}
                  </div>}
                  {message.contentType === 'ReceiptProposal' && <div className="assistant-artifact">
                    <Icon name="journal" /><span>{t('assistant.pendingReceipt')}</span>
                    {action?.receiptId && <Link to="/console/receivables" onClick={onClose}>{t('common.view')}</Link>}
                    {canApprove && proposed && message.proposalId && <button type="button" onClick={() => confirmProposal.mutate(message.proposalId ?? '')} disabled={confirmProposal.isPending}>
                      {t('assistant.confirmProposal')}
                    </button>}
                    {action && action.status !== 'Proposed' && <small>{action.status}</small>}
                  </div>}
                  {message.contentType === 'InvoiceProposal' && <div className="assistant-artifact">
                    <Icon name="receivables" /><span>{t('assistant.pendingInvoice')}</span>
                    {action?.invoiceId && <Link to="/console/receivables" onClick={onClose}>{t('common.view')}</Link>}
                    {canApprove && proposed && message.proposalId && <button type="button" onClick={() => confirmProposal.mutate(message.proposalId ?? '')} disabled={confirmProposal.isPending}>
                      {t('assistant.confirmProposal')}
                    </button>}
                    {action && action.status !== 'Proposed' && <small>{action.status}</small>}
                  </div>}
                  {message.contentType === 'SupplierPaymentProposal' && <div className="assistant-artifact">
                    <Icon name="payables" /><span>{t('assistant.pendingSupplierPayment')}</span>
                    {action?.supplierPaymentId && <Link to="/console/payables" onClick={onClose}>{t('common.view')}</Link>}
                    {canApprove && proposed && message.proposalId && <button type="button" onClick={() => confirmProposal.mutate(message.proposalId ?? '')} disabled={confirmProposal.isPending}>
                      {t('assistant.confirmProposal')}
                    </button>}
                    {action && action.status !== 'Proposed' && <small>{action.status}</small>}
                  </div>}
                  {message.contentType === 'BankTransferProposal' && <div className="assistant-artifact">
                    <Icon name="exchange" /><span>{t('assistant.pendingBankTransfer')}</span>
                    {action?.bankTransferId && <Link to="/console/treasury" onClick={onClose}>{t('common.view')}</Link>}
                    {canApprove && proposed && message.proposalId && <button type="button" onClick={() => confirmProposal.mutate(message.proposalId ?? '')} disabled={confirmProposal.isPending}>
                      {t('assistant.confirmProposal')}
                    </button>}
                    {action && action.status !== 'Proposed' && <small>{action.status}</small>}
                  </div>}
                  {message.contentType === 'CashTransactionProposal' && <div className="assistant-artifact">
                    <Icon name="cash" /><span>{t('assistant.pendingCashTransaction')}</span>
                    {action?.cashTransactionId && <Link to="/console/treasury" onClick={onClose}>{t('common.view')}</Link>}
                    {canApprove && proposed && message.proposalId && <button type="button" onClick={() => confirmProposal.mutate(message.proposalId ?? '')} disabled={confirmProposal.isPending}>
                      {t('assistant.confirmProposal')}
                    </button>}
                    {action && action.status !== 'Proposed' && <small>{action.status}</small>}
                  </div>}
                  {message.contentType === 'StatementImportProposal' && <div className="assistant-artifact">
                    <Icon name="reports" /><span>{t('assistant.pendingStatementImport')}</span>
                    {action?.statementImportId && <Link to="/console/treasury" onClick={onClose}>{t('common.view')}</Link>}
                    {canApprove && proposed && message.proposalId && <button type="button" onClick={() => confirmProposal.mutate(message.proposalId ?? '')} disabled={confirmProposal.isPending}>
                      {t('assistant.confirmProposal')}
                    </button>}
                    {action && action.status !== 'Proposed' && <small>{action.status}</small>}
                  </div>}
                  {message.contentType === 'PersonProposal' && <div className="assistant-artifact">
                    <Icon name="user" /><span>{t('assistant.pendingPerson')}</span>
                    <Link to="/console/persons" onClick={onClose}>{t('common.view')}</Link>
                    {canApprove && proposed && message.proposalId && <button type="button" onClick={() => confirmProposal.mutate(message.proposalId ?? '')} disabled={confirmProposal.isPending}>
                      {t('assistant.confirmPerson')}
                    </button>}
                    {action && action.status !== 'Proposed' && <small>{action.status}</small>}
                  </div>}
                  <footer className="assistant-message__foot">
                    <time dateTime={message.createdAt}>{formatClock(message.createdAt, locale)}</time>
                    {message.role === 'Assistant' && message.traceId && configuration.data?.canInspectEvidence && <button className="assistant-evidence-link" type="button" onClick={() => setSelectedTraceId(message.traceId)}><Icon name="search" />{t('assistant.evidence')}</button>}
                    {message.role === 'Assistant' && <button className="assistant-speak" type="button" onClick={() => void speak(message).catch(() => setVoiceError(t('assistant.speechFailed')))} aria-label={t('assistant.speak')}><Icon name="speaker" /></button>}
                  </footer>
                </article>
              );})}
              {pendingMessage && (
                <article className="assistant-message assistant-message--user assistant-message--pending" aria-label={t('assistant.sending')}>
                  <p>{pendingMessage}</p>
                </article>
              )}
              {streamedContent && (
                <article className="assistant-message assistant-message--assistant assistant-message--pending" aria-live="polite">
                  <p>{streamedContent}</p>
                </article>
              )}
              {busy && <div className="assistant-thinking"><i /><i /><i /><span>{transcribing ? t('assistant.transcribing') : t('assistant.thinking')}</span></div>}
              <div ref={messagesEnd} />
            </div>
            <form className="assistant-composer" onSubmit={submit}>
              {recording && recorder.current !== null && (
                <div className="assistant-recorder">
                  <span className="assistant-recorder__wave" aria-hidden="true"><i /><i /><i /><i /><i /></span>
                  <strong role="status">{t('assistant.recording')}</strong>
                  <span className="assistant-recorder__clock">
                    {formatDuration(recordedMilliseconds)} / {formatDuration(MAXIMUM_RECORDING_MILLISECONDS)}
                  </span>
                  <button className="icon-button" type="button" onClick={() => finishRecording(true)} aria-label={t('assistant.discardRecording')}><Icon name="trash" /></button>
                  <button className="assistant-recorder__stop" type="button" onClick={() => finishRecording(false)}>
                    <Icon name="stop" /><span>{t('assistant.stopAndSend')}</span>
                  </button>
                  <span className="assistant-recorder__track" aria-hidden="true">
                    <i style={{ width: `${Math.min(100, (recordedMilliseconds / MAXIMUM_RECORDING_MILLISECONDS) * 100)}%` }} />
                  </span>
                </div>
              )}
              <div className="assistant-composer__shell">
                <textarea
                  ref={composer}
                  value={draft}
                  onChange={(event) => setDraft(event.target.value)}
                  onKeyDown={composerKeyDown}
                  rows={2}
                  maxLength={8000}
                  placeholder={t('assistant.placeholder')}
                  disabled={disabled || recording}
                />
                <div className="assistant-composer__actions">
                  {!voiceHeldByShell && (
                    <button
                      className={`assistant-composer__mic${recording ? ' recording' : ''}`}
                      type="button"
                      onClick={() => void toggleVoice().catch(() => setVoiceError(t('assistant.voiceFailed')))}
                      aria-label={recording ? t('assistant.stopAndSend') : t('assistant.voice')}
                      aria-pressed={recording}
                      disabled={busy || disabled || !voiceReady}
                    >
                      <Icon name={recording ? 'stop' : 'microphone'} />
                    </button>
                  )}
                  <button
                    className="assistant-composer__send"
                    type="submit"
                    aria-label={t('assistant.send')}
                    disabled={!draft.trim() || busy || disabled}
                  >
                    <Icon name="send" />
                  </button>
                </div>
              </div>
              <div className="assistant-composer__meta">
                {/* The speaker is the shell's switch when the shell has one; there is one of it. */}
                {configuration.data?.textToSpeechEnabled && !voiceHeldByShell && <label className="assistant-audio-reply">
                  <input type="checkbox" checked={audioReply} onChange={(event) => setAudioReply(event.target.checked)} />
                  <span>{t('assistant.audioReply')}</span>
                </label>}
                <small className={sendError || voiceError ? 'assistant-composer-error' : ''} role={sendError ? 'alert' : 'status'}>
                  {sendError
                    || voiceError
                    || (recording ? t('assistant.recordingLimit') : '')
                    || (transcribing ? t('assistant.transcribing') : '')
                    // The review notice is the resting state: it is a compliance promise, not a tip, so it
                    // stays on screen until the box has something in it and the shortcut becomes useful.
                    || (draft.trim() ? t('assistant.sendHint') : t('assistant.reviewNotice'))}
                </small>
              </div>
            </form>
          </div>
        ) : tab === 'advisories' ? (
          <AdvisoryPanel canManage={configuration.data?.canManageAdvisories === true} locale={locale} />
        ) : tab === 'history' ? (
          <HistoryPanel
            conversations={conversations.data ?? []}
            activeId={activeId}
            locale={locale}
            onOpen={(id) => { setActiveId(id); setTab('chat'); }}
            onCreate={() => { createConversation.mutate(); setTab('chat'); }}
          />
        ) : tab === 'schedules' ? <SchedulePanel schedules={schedules.data ?? []} />
          : tab === 'memory' ? <MemoryPanel /> : tab === 'resources'
            ? <AssistantResourcePanel canManage={configuration.data?.canManageResourcePolicies === true} />
            : <KnowledgePanel locale={locale} />}
        {selectedTraceId && <EvidenceDrawer trace={selectedTrace.data} loading={selectedTrace.isLoading} usage={usage.data} onClose={() => setSelectedTraceId(null)} onConfirm={(proposalId) => confirmProposal.mutate(proposalId)} onReject={(proposalId) => rejectProposal.mutate(proposalId)} busy={confirmProposal.isPending || rejectProposal.isPending} />}
      </aside>
    </>
  );
}

/**
 * The sash on the panel's inner edge.
 *
 * The docked panel is flush with the viewport edge, so the width the pointer is asking for is
 * simply its distance from that edge — and which edge that is depends on the language on screen,
 * because in Persian the assistant docks to the left rather than the right.
 */
function AssistantSash({ width, onResize }: { width: number; onResize: (width: number) => void }) {
  const { t } = useTranslation();
  const rightToLeft = () => document.documentElement.dir === 'rtl';

  const release = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
    document.body.classList.remove('is-dock-resizing-x');
  };

  return (
    <div
      className="assistant-sash"
      role="separator"
      tabIndex={0}
      aria-orientation="vertical"
      aria-label={t('assistant.resize')}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round((width / Math.max(window.innerWidth, 1)) * 100)}
      onPointerDown={(event) => {
        if (event.button !== 0) return;
        event.preventDefault();
        event.currentTarget.setPointerCapture(event.pointerId);
        document.body.classList.add('is-dock-resizing-x');
      }}
      onPointerMove={(event) => {
        if (!event.currentTarget.hasPointerCapture(event.pointerId)) return;
        onResize(rightToLeft() ? event.clientX : window.innerWidth - event.clientX);
      }}
      onPointerUp={release}
      onPointerCancel={release}
      onKeyDown={(event) => {
        const grow = event.key === (rightToLeft() ? 'ArrowRight' : 'ArrowLeft');
        const shrink = event.key === (rightToLeft() ? 'ArrowLeft' : 'ArrowRight');
        if (!grow && !shrink) return;
        event.preventDefault();
        onResize(width + (grow ? resizeStep : -resizeStep));
      }}
    >
      <span className="assistant-sash__grip" aria-hidden="true" />
    </div>
  );
}

function AdvisoryPanel({ canManage, locale }: { canManage: boolean; locale: AppLocale }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ['assistant', 'advisories'], queryFn: () => assistantApi.advisories(false), retry: false,
  });
  const dismiss = useMutation({
    mutationFn: assistantApi.dismissAdvisory,
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['assistant', 'advisories'] }),
  });
  return <div className="assistant-advisories" id="assistant-tabpanel" role="tabpanel" aria-labelledby="assistant-tab-advisories">
    <header><div><strong>{t('assistant.advisoryTitle')}</strong><small>{t('assistant.advisoryHint')}</small></div><button className="icon-button" type="button" onClick={() => void query.refetch()} aria-label={t('common.refresh')}><Icon name="exchange" /></button></header>
    {query.isLoading ? <div className="assistant-thinking"><i /><i /><i /></div>
      : query.isError ? <div className="assistant-empty"><Icon name="alert" /><strong>{t('assistant.advisoryFailed')}</strong></div>
        : query.data?.length === 0 ? <div className="assistant-empty"><Icon name="check" /><strong>{t('assistant.noAdvisories')}</strong><p>{t('assistant.noAdvisoriesHint')}</p></div>
          : <div className="assistant-advisory-list">{query.data?.map((item) => { const copy = advisoryCopy(item, t, locale); return <article key={item.id} data-severity={item.severity.toLowerCase()}>
            <header><span><Icon name={item.severity === 'Critical' ? 'alert' : 'bell'} /><strong>{copy.title}</strong></span><b>{t(`assistant.advisorySeverity.${item.severity}`)}</b></header>
            <p>{copy.message}</p>
            <footer><time dateTime={item.updatedAt}>{formatRelativeTime(item.updatedAt, locale)}</time>{canManage && item.status === 'Active' && <button type="button" onClick={() => dismiss.mutate(item.id)} disabled={dismiss.isPending}>{t('assistant.dismissAdvisory')}</button>}</footer>
          </article>; })}</div>}
  </div>;
}

function advisoryCopy(item: AssistantAdvisory, t: TFunction, locale: AppLocale) {
  let payload: Record<string, unknown> = {};
  try { payload = JSON.parse(item.payloadJson) as Record<string, unknown>; } catch { return { title: item.title, message: item.message }; }
  const number = (value: unknown) => typeof value === 'number'
    ? value.toLocaleString(calendarLocaleTag(locale), { maximumFractionDigits: 2 }) : String(value ?? '');
  const values = Object.fromEntries(Object.entries(payload).map(([key, value]) => [key, number(value)]));
  const known = new Set(['OverdueReceivables', 'OverduePayables', 'LowStock', 'CashLimit',
    'ReconciliationException', 'PossibleDuplicateSupplierBill']);
  if (!known.has(item.advisoryType)) return { title: item.title, message: item.message };
  return {
    title: t(`assistant.advisoryCopy.${item.advisoryType}Title`, values),
    message: t(`assistant.advisoryCopy.${item.advisoryType}Message`, values),
  };
}

/**
 * Everything already said, in one place. The conversation switcher used to be a dropdown wedged beside the
 * composer, which meant older threads were only reachable by reading truncated titles in a list you had to
 * open first. As a tab it can show when each thread was last touched and be searched.
 */
function HistoryPanel({ conversations, activeId, locale, onOpen, onCreate }: {
  conversations: readonly AssistantConversation[]; activeId: string | null; locale: AppLocale;
  onOpen: (id: string) => void; onCreate: () => void;
}) {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const matches = useMemo(() => {
    const needle = normalizeSearchText(query);
    const ordered = [...conversations].sort((left, right) => right.updatedAt.localeCompare(left.updatedAt));
    return needle ? ordered.filter((item) => normalizeSearchText(item.title).includes(needle)) : ordered;
  }, [conversations, query]);

  return (
    <div className="assistant-history" id="assistant-tabpanel" role="tabpanel" aria-labelledby="assistant-tab-history">
      <header>
        <div><strong>{t('assistant.history')}</strong><small>{t('assistant.historyHint')}</small></div>
        <button className="button button--primary" type="button" onClick={onCreate}>
          <Icon name="plus" /><span>{t('assistant.newConversation')}</span>
        </button>
      </header>
      <label className="assistant-history__search">
        <Icon name="search" />
        <input
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder={t('assistant.historySearch')}
          aria-label={t('assistant.historySearch')}
        />
      </label>
      {matches.length === 0 ? (
        <div className="assistant-empty">
          <Icon name="history" />
          <strong>{t('assistant.historyEmpty')}</strong>
          <p>{t('assistant.historyEmptyHint')}</p>
        </div>
      ) : (
        <ul className="assistant-history__list" aria-label={t('assistant.conversations')}>
          {matches.map((item) => (
            <li key={item.id}>
              <button
                type="button"
                className={item.id === activeId ? 'active' : ''}
                onClick={() => onOpen(item.id)}
                aria-label={t('assistant.openConversation')}
              >
                <span className="assistant-history__title">{item.title}</span>
                <span className="assistant-history__meta">
                  <time dateTime={item.updatedAt}>{formatRelativeTime(item.updatedAt, locale)}</time>
                  {item.id === activeId && <b>{t('assistant.activeConversation')}</b>}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function EvidenceDrawer({ trace, loading, usage, onClose, onConfirm, onReject, busy }: {
  trace: AssistantTrace | undefined; loading: boolean; usage: Awaited<ReturnType<typeof assistantApi.usage>> | undefined;
  onClose: () => void; onConfirm: (proposalId: string) => void; onReject: (proposalId: string) => void; busy: boolean;
}) {
  const { t } = useTranslation();
  return <section className="assistant-evidence-drawer" aria-label={t('assistant.evidence')}><header><div><strong>{t('assistant.evidence')}</strong>{trace && <small>{trace.agentCode}</small>}</div><button className="icon-button" type="button" onClick={onClose}><Icon name="close" /></button></header>
    {loading ? <div className="assistant-thinking"><i /><i /><i /></div> : trace && <div className="assistant-evidence-body">
      <dl><div><dt>{t('assistant.intent')}</dt><dd>{trace.intent}</dd></div><div><dt>{t('assistant.risk')}</dt><dd>{trace.riskLevel}</dd></div><div><dt>{t('assistant.duration')}</dt><dd>{trace.durationMilliseconds} ms</dd></div><div><dt>{t('assistant.tokens')}</dt><dd>{trace.inputTokens + trace.outputTokens}</dd></div></dl>
      {trace.action && <article className="assistant-action-card"><header><strong>{t('assistant.actionProposal')}</strong><span>{trace.action.riskLevel}</span></header><p>{trace.action.explanation}</p><pre>{trace.action.expectedEffectJson}</pre><small>{trace.action.status}</small>{trace.action.status === 'Proposed' && <div><button type="button" onClick={() => onConfirm(trace.action!.id)} disabled={busy}>{t('assistant.confirmProposal')}</button><button type="button" onClick={() => onReject(trace.action!.id)} disabled={busy}>{t('common.reject')}</button></div>}</article>}
      <h3>{t('assistant.sources')}</h3>{trace.evidence.length === 0 ? <p>{t('assistant.noEvidence')}</p> : trace.evidence.map((item) => <article className="assistant-evidence-item" key={item.evidenceId}><strong>{item.citationLabel}</strong><small>{item.sourceTitle}{item.articleReference ? ` · ${item.articleReference}` : ''}{item.page ? ` · p. ${item.page}` : ''}</small><p>{item.text}</p><meter min="0" max="1" value={item.score} /></article>)}
      <h3>{t('assistant.execution')}</h3>{trace.toolCallsDetail.map((item) => <article className="assistant-tool-item" key={item.id}><strong>{item.toolName}</strong><span>{item.status} · {item.durationMilliseconds} ms</span></article>)}
      {usage && <footer><span>{t('assistant.budget')}</span><strong>{usage.tokensToday.toLocaleString()} / {usage.dailyTokenBudget.toLocaleString()}</strong><small>${usage.costThisMonth.toFixed(4)} / ${usage.monthlyCostBudget.toFixed(2)} · {usage.supervisionMode}</small></footer>}
    </div>}
  </section>;
}

function MemoryPanel() {
  const { t } = useTranslation(); const queryClient = useQueryClient();
  const query = useQuery({ queryKey: ['assistant', 'memories'], queryFn: assistantApi.memories });
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['assistant', 'memories'] });
  const approve = useMutation({ mutationFn: assistantApi.approveMemory, onSuccess: refresh });
  const remove = useMutation({ mutationFn: assistantApi.deleteMemory, onSuccess: refresh });
  return <div className="assistant-memory" id="assistant-tabpanel" role="tabpanel" aria-labelledby="assistant-tab-memory"><header><div><strong>{t('assistant.memoryTitle')}</strong><small>{t('assistant.memoryHint')}</small></div><button className="icon-button" type="button" onClick={() => void query.refetch()}><Icon name="exchange" /></button></header>
    <div>{query.data?.map((item) => <article key={item.id}><header><span><strong>{item.statement}</strong><small>{item.scope} · {item.memoryType} · {(item.confidence * 100).toFixed(0)}%</small></span><b>{item.status}</b></header><footer>{item.status === 'Candidate' && <button type="button" onClick={() => approve.mutate(item.id)} disabled={approve.isPending}>{t('common.approve')}</button>}<button type="button" onClick={() => remove.mutate(item.id)} disabled={remove.isPending}>{t('common.delete')}</button></footer></article>)}</div>
  </div>;
}

function KnowledgePanel({ locale }: { locale: AppLocale }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [title, setTitle] = useState('');
  const [language, setLanguage] = useState(speechLocaleTag(locale));
  const [file, setFile] = useState<File | null>(null);
  const query = useQuery({ queryKey: ['assistant', 'knowledge'], queryFn: assistantApi.knowledge });
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['assistant', 'knowledge'] });
  const upload = useMutation({
    mutationFn: () => assistantApi.uploadKnowledge(title.trim(), language, file!),
    onSuccess: () => { setTitle(''); setFile(null); void refresh(); },
  });
  const publish = useMutation({ mutationFn: assistantApi.publishKnowledge, onSuccess: refresh });
  return <div className="assistant-knowledge" id="assistant-tabpanel" role="tabpanel" aria-labelledby="assistant-tab-knowledge">
    <form onSubmit={(event) => { event.preventDefault(); if (file && title.trim()) upload.mutate(); }}>
      <header><strong>{t('assistant.knowledgeTitle')}</strong><small>{t('assistant.knowledgeHint')}</small></header>
      <label><span>{t('assistant.knowledgeDocumentTitle')}</span><input value={title} onChange={(event) => setTitle(event.target.value)} maxLength={240} required /></label>
      <label><span>{t('assistant.knowledgeLanguage')}</span><select value={language} onChange={(event) => setLanguage(event.target.value as typeof language)}><option value="fa-IR">{t('context.persian')}</option><option value="ar-SA">{t('context.arabic')}</option><option value="en-US">{t('context.english')}</option></select></label>
      <label><span>{t('assistant.knowledgeFile')}</span><input type="file" accept=".pdf,.docx,.html,.htm,.md,.txt,.csv" onChange={(event) => setFile(event.target.files?.[0] ?? null)} required /></label>
      <button className="button button--primary" type="submit" disabled={!file || !title.trim() || upload.isPending}>{upload.isPending ? t('assistant.processingKnowledge') : t('assistant.uploadKnowledge')}</button>
      {upload.isError && <em>{t('assistant.knowledgeUploadFailed')}</em>}
    </form>
    <div className="assistant-knowledge-list">{query.data?.map((document) => <article key={document.id}>
      <div><strong>{document.title}</strong><small>{document.language} · {document.characterCount.toLocaleString()} {t('assistant.characters')} · {document.chunkCount.toLocaleString()} {t('assistant.chunks')}</small></div>
      <b>{document.status}</b>
      {document.status !== 'Published' && <button type="button" onClick={() => publish.mutate(document.id)} disabled={publish.isPending}>{t('assistant.publishKnowledge')}</button>}
    </article>)}</div>
  </div>;
}

function AssistantWelcome({ onPrompt }: { onPrompt: (value: string) => void }) {
  const { t } = useTranslation();
  const prompts = useMemo(() => [t('assistant.promptTransaction'), t('assistant.promptReport'), t('assistant.promptReview')], [t]);
  return <div className="assistant-welcome">
    <span className="assistant-welcome__mark" aria-hidden="true" />
    <h2>{t('assistant.welcome')}</h2><p>{t('assistant.welcomeHint')}</p>
    <div>{prompts.map((prompt) => <button type="button" key={prompt} onClick={() => onPrompt(prompt)}>{prompt}</button>)}</div>
  </div>;
}

function SchedulePanel({ schedules }: { schedules: readonly AssistantSchedule[] }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [prompt, setPrompt] = useState('');
  const [intervalMinutes, setIntervalMinutes] = useState(1440);
  const create = useMutation({
    mutationFn: assistantApi.createSchedule,
    onSuccess: () => { setName(''); setPrompt(''); void queryClient.invalidateQueries({ queryKey: ['assistant', 'schedules'] }); },
  });
  const toggle = useMutation({
    mutationFn: ({ schedule, enabled }: { schedule: AssistantSchedule; enabled: boolean }) => assistantApi.setScheduleState(schedule, enabled),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['assistant', 'schedules'] }),
  });
  return <div className="assistant-schedules" id="assistant-tabpanel" role="tabpanel" aria-labelledby="assistant-tab-schedules">
    <form onSubmit={(event) => { event.preventDefault(); create.mutate({ name, prompt, intervalMinutes }); }}>
      <label><span>{t('assistant.scheduleName')}</span><input value={name} onChange={(event) => setName(event.target.value)} required maxLength={160} /></label>
      <label><span>{t('assistant.schedulePrompt')}</span><textarea value={prompt} onChange={(event) => setPrompt(event.target.value)} required rows={4} /></label>
      <label><span>{t('assistant.interval')}</span><select value={intervalMinutes} onChange={(event) => setIntervalMinutes(Number(event.target.value))}>
        <option value={60}>{t('assistant.hourly')}</option><option value={1440}>{t('assistant.daily')}</option><option value={10080}>{t('assistant.weekly')}</option>
      </select></label>
      <button className="button button--primary" disabled={create.isPending}>{t('assistant.createSchedule')}</button>
    </form>
    <div className="assistant-schedule-list">{schedules.map((schedule) => <article key={schedule.id}>
      <div><strong>{schedule.name}</strong><small>{schedule.prompt}</small>{schedule.lastError && <em>{schedule.lastError}</em>}</div>
      <button type="button" onClick={() => toggle.mutate({ schedule, enabled: !schedule.isEnabled })}>{schedule.isEnabled ? t('assistant.pause') : t('assistant.resume')}</button>
    </article>)}</div>
  </div>;
}
